import { useState, useEffect } from 'react';
import { authAxios } from '../../contexts/AuthContext';
import { ChevronLeft, ChevronRight, MapPin, Clock, Globe } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { AdminHeader } from '../../components/AdminHeader';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminLoginHistory() {
  const [logs, setLogs] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pages, setPages] = useState(1);
  const [loading, setLoading] = useState(true);

  useEffect(() => { fetchLogs(); }, [page]);

  const fetchLogs = async () => {
    setLoading(true);
    try {
      const { data } = await authAxios.get(`${API}/admin/login-history?page=${page}&limit=30`);
      setLogs(data.logs); setTotal(data.total); setPages(data.pages);
    } catch (err) { console.error(err); }
    setLoading(false);
  };

  return (
    <div className="min-h-screen bg-[#F5F5F5]">
      <AdminHeader />

      <div className="max-w-2xl mx-auto px-4 py-5 sm:py-8">
        <div className="flex items-center justify-between mb-5">
          <div>
            <h1 className="text-2xl font-bold text-[#1C1C1E]">Login Activity</h1>
            <p className="text-[13px] text-[#8E8E93] mt-0.5">{total} total logins tracked</p>
          </div>
        </div>

        {loading ? <p className="text-[#8E8E93]">Loading...</p> : logs.length === 0 ? (
          <div className="bg-white rounded-2xl text-center p-8 shadow-sm">
            <Globe className="w-8 h-8 text-[#C7C7CC] mx-auto mb-3" strokeWidth={1.5} />
            <p className="text-[#8E8E93] text-sm">No login activity yet</p>
          </div>
        ) : (
          <div className="bg-white rounded-2xl overflow-hidden shadow-sm">
            {logs.map((log, i) => {
              const loc = log.location || {};
              const locationStr = [loc.city, loc.region, loc.country].filter(Boolean).join(', ') || 'Unknown location';
              return (
                <div key={i} className={`px-4 py-3.5 ${i < logs.length - 1 ? 'border-b border-[#F2F2F7]' : ''}`}>
                  <div className="flex items-center justify-between mb-1.5">
                    <div className="flex items-center gap-2">
                      <span className="text-[15px] font-semibold text-[#1C1C1E]">{log.name}</span>
                      <Badge className={`${log.role === 'admin' ? 'bg-purple-50 text-purple-700' : 'bg-blue-50 text-blue-700'} border-0 text-[10px]`}>
                        {log.role}
                      </Badge>
                    </div>
                    <span className="text-[11px] text-[#8E8E93]">@{log.username}</span>
                  </div>
                  <div className="flex items-center gap-4 text-[12px] text-[#8E8E93]">
                    <div className="flex items-center gap-1">
                      <Clock className="w-3 h-3" strokeWidth={1.5} />
                      <span>{new Date(log.timestamp).toLocaleString('en-IN', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })}</span>
                    </div>
                    <div className="flex items-center gap-1">
                      <MapPin className="w-3 h-3" strokeWidth={1.5} />
                      <span>{locationStr}</span>
                    </div>
                  </div>
                  <div className="text-[11px] text-[#C7C7CC] mt-0.5">IP: {loc.ip || 'N/A'}</div>
                </div>
              );
            })}
          </div>
        )}

        {pages > 1 && (
          <div className="flex items-center justify-between mt-4">
            <p className="text-[13px] text-[#8E8E93]">Page {page} of {pages}</p>
            <div className="flex gap-2">
              <Button variant="outline" size="sm" className="border-[#E5E5EA] rounded-full h-8" disabled={page <= 1} onClick={() => setPage(p => p - 1)}>
                <ChevronLeft className="w-4 h-4" />
              </Button>
              <Button variant="outline" size="sm" className="border-[#E5E5EA] rounded-full h-8" disabled={page >= pages} onClick={() => setPage(p => p + 1)}>
                <ChevronRight className="w-4 h-4" />
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
